import { useState, useCallback, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/components/ui/use-toast';
import { Search, FileJson, AlertTriangle, Plus } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

interface AvailableMeasure {
  measure_id: string;
  parent_measure_id: string | null;
  measure_type: string;
  measure_title: string;
  measure_frequency: string;
  stratum: string | null;
  submission_method: string;
  inverse: boolean;
  year: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  is_latest_version: boolean;
  version_count: number;
}

interface AddMeasuresModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function AddMeasuresModal({ open, onOpenChange }: AddMeasuresModalProps) {
  const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [yearFilter, setYearFilter] = useState<string>('all');
  const [versionFilter, setVersionFilter] = useState<'latest' | 'all'>('latest');
  const [selectedMeasures, setSelectedMeasures] = useState<Set<string>>(new Set());
  
  // Conflict dialog state
  const [showConflictDialog, setShowConflictDialog] = useState(false);
  const [conflictInfo, setConflictInfo] = useState<{
    alreadyActive: AvailableMeasure[];
    toReactivate: AvailableMeasure[];
  } | null>(null);

  // Fetch available (inactive) measures
  const { data: availableMeasures, isLoading } = useQuery<AvailableMeasure[]>({
    queryKey: ['available-measures'],
    queryFn: async () => {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${apiUrl}/api/measures/available`, {
        headers: {
          'Authorization': token ? `Bearer ${token}` : '',
          'Accept': 'application/json'
        }
      });
      if (!response.ok) {
        throw new Error('Failed to fetch available measures');
      }
      return response.json();
    },
    enabled: open // Only fetch when modal is open
  });

  // Fetch active measures for conflict checking
  const { data: activeMeasures } = useQuery<Array<{measure_id: string; measure_title: string}>>({
    queryKey: ['measures'],
    queryFn: async () => {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${apiUrl}/api/measures/list`, {
        headers: {
          'Authorization': token ? `Bearer ${token}` : '',
          'Accept': 'application/json'
        }
      });
      if (!response.ok) {
        throw new Error('Failed to fetch active measures');
      }
      return response.json();
    },
    enabled: open // Only fetch when modal is open
  });

  // Reactivate measures mutation
  const reactivateMutation = useMutation({
    mutationFn: async (measures: AvailableMeasure[]) => {
      const token = localStorage.getItem('auth_token');
      const measureIds = measures.map(m => ({
        measure_id: m.measure_id,
        year: m.year,
        updated_at: m.updated_at
      }));
      
      const response = await fetch(`${apiUrl}/api/measures/reactivate`, {
        method: 'POST',
        headers: {
          'Authorization': token ? `Bearer ${token}` : '',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ measureIds })
      });
      
      if (!response.ok) {
        throw new Error('Failed to reactivate measures');
      }
      return response.json();
    },
    onSuccess: (data) => {
      const successCount = data.results.filter((r: any) => r.success).length;
      const warnings = data.results.filter((r: any) => r.warning);
      
      toast({
        title: 'Measures reactivated',
        description: `Successfully reactivated ${successCount} measure${successCount === 1 ? '' : 's'}.${warnings.length > 0 ? ` ${warnings.length} warning(s) - check console.` : ''}`
      });
      
      // Log warnings to console
      warnings.forEach((w: any) => console.warn(w.warning));
      
      queryClient.invalidateQueries({ queryKey: ['measures'] });
      queryClient.invalidateQueries({ queryKey: ['available-measures'] });
      clearSelection();
      onOpenChange(false);
    },
    onError: (error) => {
      toast({
        title: 'Reactivation failed',
        description: error instanceof Error ? error.message : 'Failed to reactivate measures',
        variant: 'destructive'
      });
    }
  });

  // Get unique years for filter
  const availableYears = useMemo(() => {
    if (!availableMeasures) return [];
    const years = [...new Set(availableMeasures.map(m => m.year))];
    return years.sort((a, b) => b - a); // Descending order
  }, [availableMeasures]);

  // Filter and process measures
  const filteredMeasures = useMemo(() => {
    if (!availableMeasures) return [];
    
    let filtered = availableMeasures.filter(measure => {
      // Search filter
      const matchesSearch = measure.measure_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           measure.measure_title.toLowerCase().includes(searchTerm.toLowerCase());
      
      // Year filter
      const matchesYear = yearFilter === 'all' || measure.year.toString() === yearFilter;
      
      // Version filter
      const matchesVersion = versionFilter === 'all' || measure.is_latest_version;
      
      return matchesSearch && matchesYear && matchesVersion;
    });
    
    return filtered.sort((a, b) => {
      // Sort by measure_id, then year (desc), then updated_at (desc)
      if (a.measure_id !== b.measure_id) {
        return a.measure_id.localeCompare(b.measure_id);
      }
      if (a.year !== b.year) {
        return b.year - a.year;
      }
      return new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime();
    });
  }, [availableMeasures, searchTerm, yearFilter, versionFilter]);

  // Selection helpers
  const toggleMeasureSelection = useCallback((measure: AvailableMeasure) => {
    const key = `${measure.measure_id}-${measure.year}-${measure.updated_at}`;
    setSelectedMeasures(prev => {
      const next = new Set(prev);
      
      // If this measure is already selected, deselect it
      if (next.has(key)) {
        next.delete(key);
      } else {
        // Before adding, remove any other versions of the same measure_id
        // This ensures only one version per measure_id can be selected
        Array.from(next).forEach(existingKey => {
          const [existingMeasureId] = existingKey.split('-');
          if (existingMeasureId === measure.measure_id) {
            next.delete(existingKey);
          }
        });
        next.add(key);
      }
      return next;
    });
  }, []);

  const selectAllMeasures = useCallback(() => {
    // When selecting all, only select the latest version of each unique measure_id
    const measuresByIdMap = new Map<string, AvailableMeasure>();
    
    filteredMeasures.forEach(measure => {
      const existing = measuresByIdMap.get(measure.measure_id);
      if (!existing || 
          measure.year > existing.year || 
          (measure.year === existing.year && new Date(measure.updated_at) > new Date(existing.updated_at))) {
        measuresByIdMap.set(measure.measure_id, measure);
      }
    });
    
    const allKeys = Array.from(measuresByIdMap.values()).map(
      m => `${m.measure_id}-${m.year}-${m.updated_at}`
    );
    setSelectedMeasures(new Set(allKeys));
  }, [filteredMeasures]);

  const clearSelection = useCallback(() => {
    setSelectedMeasures(new Set());
  }, []);

  const getSelectedMeasures = useCallback(() => {
    return filteredMeasures.filter(m => 
      selectedMeasures.has(`${m.measure_id}-${m.year}-${m.updated_at}`)
    );
  }, [filteredMeasures, selectedMeasures]);

  const handleReactivate = () => {
    const selected = getSelectedMeasures();
    if (selected.length === 0) return;
    
    // Check for conflicts with already active measures
    if (activeMeasures) {
      const activeMeasureIds = new Set(activeMeasures.map(m => m.measure_id));
      const alreadyActive = selected.filter(m => activeMeasureIds.has(m.measure_id));
      const toReactivate = selected.filter(m => !activeMeasureIds.has(m.measure_id));
      
      if (alreadyActive.length > 0) {
        // Show conflict dialog
        setConflictInfo({ alreadyActive, toReactivate });
        setShowConflictDialog(true);
        return;
      }
    }
    
    // No conflicts, proceed with reactivation
    reactivateMutation.mutate(selected);
  };

  const handleConfirmReactivation = () => {
    if (conflictInfo && conflictInfo.toReactivate.length > 0) {
      reactivateMutation.mutate(conflictInfo.toReactivate);
    }
    setShowConflictDialog(false);
    setConflictInfo(null);
  };

  // Reset state when modal closes
  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      setSearchTerm('');
      setYearFilter('all');
      setVersionFilter('latest');
      clearSelection();
      setShowConflictDialog(false);
      setConflictInfo(null);
    }
    onOpenChange(newOpen);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="max-w-6xl h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Plus className="h-5 w-5" />
            Add Measures
          </DialogTitle>
          <DialogDescription>
            Select inactive measures to reactivate. Only inactive measures are shown here.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-hidden flex flex-col space-y-4">
          {/* Filters */}
          <div className="flex items-center gap-4 flex-wrap">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
              <Input
                placeholder="Search by measure ID or title..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>
            
            <Select value={yearFilter} onValueChange={setYearFilter}>
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder="All Years" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Years</SelectItem>
                {availableYears.map(year => (
                  <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <RadioGroup
              value={versionFilter}
              onValueChange={(value: 'latest' | 'all') => setVersionFilter(value)}
              className="flex items-center gap-4"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="latest" id="latest" />
                <Label htmlFor="latest" className="text-sm">Latest versions only</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="all" id="all" />
                <Label htmlFor="all" className="text-sm">All versions</Label>
              </div>
            </RadioGroup>

            <Badge variant="secondary" className="px-3 py-1">
              {filteredMeasures.length} available
            </Badge>
          </div>

          {/* Selection bar */}
          {selectedMeasures.size > 0 && (
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center justify-between">
                <span className="text-sm text-blue-700">
                  {selectedMeasures.size} measure{selectedMeasures.size === 1 ? '' : 's'} selected
                </span>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={clearSelection}>
                    Clear Selection
                  </Button>
                  <Button 
                    size="sm" 
                    onClick={handleReactivate}
                    disabled={reactivateMutation.isPending}
                  >
                    {reactivateMutation.isPending ? 'Reactivating...' : `Reactivate Selected (${selectedMeasures.size})`}
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Table */}
          <div className="flex-1 overflow-auto border rounded-md min-h-[400px] relative">
            {isLoading ? (
              <div className="p-4 space-y-4">
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-64 w-full" />
              </div>
            ) : (
              <Table>
                <TableHeader className="sticky top-0 bg-background z-10 border-b">
                  <TableRow>
                    <TableHead className="text-center w-12">
                      <input
                        type="checkbox"
                        className="rounded border-gray-300"
                        checked={selectedMeasures.size > 0}
                        ref={(el) => {
                          if (el) {
                            // Check if we have selected all unique measure IDs (not all versions)
                            const uniqueMeasureIds = new Set(filteredMeasures.map(m => m.measure_id));
                            const selectedMeasureIds = new Set(
                              Array.from(selectedMeasures).map(key => key.split('-')[0])
                            );
                            const allSelected = uniqueMeasureIds.size > 0 && 
                                              uniqueMeasureIds.size === selectedMeasureIds.size;
                            el.indeterminate = selectedMeasures.size > 0 && !allSelected;
                            el.checked = allSelected;
                          }
                        }}
                        onChange={(e) => {
                          if (selectedMeasures.size > 0) {
                            // If any are selected, clear all
                            clearSelection();
                          } else {
                            // If none are selected, select all (latest versions)
                            selectAllMeasures();
                          }
                        }}
                      />
                    </TableHead>
                    <TableHead>Measure ID</TableHead>
                    <TableHead className="min-w-[200px]">Title</TableHead>
                    <TableHead>Stratum</TableHead>
                    <TableHead>Year</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Frequency</TableHead>
                    <TableHead>Version</TableHead>
                    <TableHead>Warnings</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredMeasures.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} className="text-center text-muted-foreground py-8">
                        {searchTerm || yearFilter !== 'all' ? 'No measures found matching your filters' : 'No inactive measures available'}
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredMeasures.map((measure, index) => {
                      const key = `${measure.measure_id}-${measure.stratum || 'none'}-${measure.year}-${measure.updated_at}-${index}`;
                      const selectKey = `${measure.measure_id}-${measure.year}-${measure.updated_at}`;
                      const isSelected = selectedMeasures.has(selectKey);
                      
                      return (
                        <TableRow key={key} className={isSelected ? 'bg-blue-50' : ''}>
                          <TableCell className="text-center w-12">
                            <input
                              type="checkbox"
                              className="rounded border-gray-300"
                              checked={isSelected}
                              onChange={() => toggleMeasureSelection(measure)}
                            />
                          </TableCell>
                          <TableCell className="font-medium">
                            <div className="flex items-center gap-2">
                              <FileJson className="h-4 w-4 text-muted-foreground" />
                              {measure.measure_id}
                            </div>
                          </TableCell>
                          <TableCell className="max-w-sm whitespace-normal break-words min-w-[200px]" title={measure.measure_title}>
                            {measure.measure_title}
                          </TableCell>
                          <TableCell>
                            {measure.stratum ? (
                              <Badge variant="outline" className="text-xs">
                                {measure.stratum}
                              </Badge>
                            ) : (
                              <span className="text-muted-foreground text-xs">-</span>
                            )}
                          </TableCell>
                          <TableCell>{measure.year}</TableCell>
                          <TableCell>
                            <Badge variant="outline" className="text-xs">
                              {measure.measure_type}
                            </Badge>
                          </TableCell>
                          <TableCell>{measure.measure_frequency}</TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <span className="text-sm text-muted-foreground">
                                {new Date(measure.updated_at).toLocaleDateString()}
                              </span>
                              {measure.is_latest_version && (
                                <Badge variant="default" className="text-xs">Latest</Badge>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            {/* Show warning if there might be conflicts */}
                            {!measure.is_latest_version && (
                              <div className="flex items-center gap-1 text-amber-600">
                                <AlertTriangle className="h-4 w-4" />
                                <span className="text-xs">Older version</span>
                              </div>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            )}
            {/* Add padding at the bottom to ensure last row is fully visible */}
            <div className="h-4"></div>
          </div>

          {/* Footer actions */}
          <div className="flex justify-between items-center pt-4 border-t">
            <div className="text-sm text-muted-foreground">
              {filteredMeasures.length} measure{filteredMeasures.length === 1 ? '' : 's'} available for reactivation
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => handleOpenChange(false)}>
                Cancel
              </Button>
              {selectedMeasures.size > 0 && (
                <Button 
                  onClick={handleReactivate}
                  disabled={reactivateMutation.isPending}
                >
                  {reactivateMutation.isPending ? 'Reactivating...' : `Reactivate ${selectedMeasures.size} Measure${selectedMeasures.size === 1 ? '' : 's'}`}
                </Button>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>

    {/* Conflict Warning Dialog */}
    <AlertDialog open={showConflictDialog} onOpenChange={setShowConflictDialog}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Some Measures Already Active</AlertDialogTitle>
          <AlertDialogDescription className="space-y-4">
            <div>
              The following measure{conflictInfo?.alreadyActive.length === 1 ? ' is' : 's are'} already active and will be skipped:
            </div>
            <ul className="list-disc pl-5 space-y-1">
              {conflictInfo?.alreadyActive.map(measure => (
                <li key={`${measure.measure_id}-${measure.year}`}>
                  <span className="font-medium">{measure.measure_id}</span> - {measure.measure_title}
                </li>
              ))}
            </ul>
            {conflictInfo && conflictInfo.toReactivate.length > 0 ? (
              <div>
                Do you want to continue and reactivate the remaining {conflictInfo.toReactivate.length} measure{conflictInfo.toReactivate.length === 1 ? '' : 's'}?
              </div>
            ) : (
              <div className="text-amber-600">
                All selected measures are already active. Please select different measures.
              </div>
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          {conflictInfo && conflictInfo.toReactivate.length > 0 && (
            <AlertDialogAction onClick={handleConfirmReactivation}>
              Continue with {conflictInfo.toReactivate.length} measure{conflictInfo.toReactivate.length === 1 ? '' : 's'}
            </AlertDialogAction>
          )}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </>
  );
}
